/**
 * @file correctionsAPI.ts
 * @brief This file contains the API functions for managing corrections.
 */

//* Import API configurations
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import the API URI constant
import { API_URI } from "../../constants/consts";

//* Import types
import {
  type Correction,
  type CorrectionStatusResponse,
  type CorrectionsResponse,
  type CorrectionDetails,
  type CreateCorrectionInput,
  type UpdateCorrectionInput,
  type CompletedCorrection,
} from "./types/types";

/**
 * @brief This object contains the API functions for managing corrections.
 */
export const correctionsAPI = {
  /**
   * @brief Creates a new Correction.
   *
   * @param data - The data for the new correction.
   * @param token - The authentication token.
   *
   * @returns A promise that resolves to the created Correction.
   *
   * @throws Error if the Correction creation fails.
   */
  create: async function (
    data: CreateCorrectionInput,
    token: string
  ): Promise<Correction> {
    const formData = new FormData();
    formData.append("description", data.description);
    formData.append("questionType", data.questionType);

    if (data.file) {
      formData.append("file", data.file);
    }

    return api
      .request({
        url: `${API_URI}/admin/private/corrections`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: formData,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error creating correction:", error, data);
        throw new Error("Failed to create Correction");
      });
  },

  /**
   * @brief Fetches the status of a Correction by ID.
   *
   * @param id - The ID of the correction.
   * @param token - The authentication token.
   *
   * @returns A promise that resolves to the Correction status.
   *
   * @throws Error if fetching the Correction status fails.
   */
  getStatus: async function (
    id: string,
    token: string
  ): Promise<CorrectionStatusResponse> {
    return api
      .request({
        url: `${API_URI}/admin/private/corrections/${id}/status`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error fetching correction status:", error);
        throw new Error("Failed to fetch Correction status");
      });
  },

  /**
   * @brief Fetches all Corrections with search and pagination.
   *
   * @param search - The search string.
   * @param page - The page number.
   * @param token - The authentication token.
   * @param perPage - The number of items per page.
   *
   * @returns A promise that resolves to the Corrections response.
   *
   * @throws Error if fetching the Corrections fails.
   */
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<CorrectionsResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/corrections?page=${page}&perPage=${perPage}${searchQuery}`;
    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          corrections: data.corrections || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching corrections:", error);
        throw new Error("Failed to fetch Corrections");
      });
  },

  /**
   * @brief Fetches a Correction by ID.
   *
   * @param id - The ID of the correction.
   * @param token - The authentication token.
   *
   * @returns A promise that resolves to the Correction details.
   *
   * @throws Error if fetching the Correction fails.
   */
  getById: async function (
    id: string,
    token: string
  ): Promise<CorrectionDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/corrections/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error fetching correction:", error);
        throw new Error("Failed to fetch Correction");
      });
  },

  /**
   * @brief Updates an existing Correction.
   *
   * @param id - The ID of the correction to update.
   * @param data - The data to update the correction with.
   * @param token - The authentication token.
   *
   * @returns A promise that resolves to the updated Correction details.
   *
   * @throws Error if updating the Correction fails.
   */
  update: async function (
    id: string,
    data: UpdateCorrectionInput,
    token: string
  ): Promise<CorrectionDetails> {
    // Inicialmente payload pode ser o objeto padrão
    let payload: FormData | UpdateCorrectionInput = data;

    // Se houver arquivo, cria um FormData e inclui os campos necessários
    if (data.file) {
      const formData = new FormData();
      formData.append("description", data.description);
      formData.append("file", data.file);
      formData.append("questionType", data.questionType);
      payload = formData;
    } else {
      payload = {
        ...data,
        questionType: data.questionType,
      };
    }

    return api
      .request({
        url: `${API_URI}/admin/private/corrections/${id}`,
        method: "POST", 
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating correction:", error);
        throw new Error("Failed to update Correction");
      });
  },

  /**
   * @brief Deletes a Correction by ID.
   *
   * @param id - The ID of the correction to delete.
   * @param token - The authentication token.
   *
   * @returns A promise that resolves when the Correction is deleted.
   *
   * @throws Error if deleting the Correction fails.
   */
  delete: async function (id: string, token: string): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/corrections/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting correction:", error);
        throw new Error("Failed to delete Correction");
      });
  },

  /**
   * @brief Fetches a Correction by ID with full data.
   *
   * @param id - The ID of the correction.
   * @param token - The authentication token.
   *
   * @returns A promise that resolves to the completed Correction.
   *
   * @throws Error if fetching the Correction fails.
   */
  getByIdFull: async function (
    id: string,
    token: string
  ): Promise<CompletedCorrection> {
    return api
      .request({
        url: `${API_URI}/admin/private/corrections/${id}/full`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => 
        response.data.data)
      .catch((error) => {
        console.error("Error fetching correction:", error);
        throw new Error("Failed to fetch Correction");
      });
  },
};

